import React, { useEffect, useState } from "react";
import axios from "axios";
import { History, Search, FileEdit, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtDt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ar-IQ", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// "سجل تعديلات الفواتير" — Invoice Edits Log
// Lists every edited invoice with old vs new total, who edited, when,
// and the auto-applied debt change. Searchable by customer/invoice.
const InvoiceEditsLogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchLog(); }, []);
  const fetchLog = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/invoice-edits-log`);
      setRows(data.items || []);
    } catch { toast.error("فشل تحميل السجل"); }
    finally { setLoading(false); }
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(r.invoice_number || "").toLowerCase().includes(q) ||
      (r.user_name || "").toLowerCase().includes(q) ||
      (r.user_phone || "").toLowerCase().includes(q) ||
      (r.market_name || "").toLowerCase().includes(q) ||
      (r.edited_by_admin_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" dir="rtl">
      <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 space-y-3">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-[#1E3A5F]" />
          <h2 className="font-black text-lg text-[#1E3A5F]">سجل تعديلات الفواتير</h2>
        </div>
        <p className="text-xs text-[#5C6E64]">
          كل فاتورة عُدّلت: تشوف الفرق بين القيمة القديمة والجديدة، التغيير على الدين، ومن أداريّ سوّى التعديل ومتى.
        </p>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث برقم الفاتورة، اسم الزبون، أو الأدمن..."
            data-testid="invoice-edits-search"
            className="w-full pr-9 pl-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] text-sm"
          />
        </div>
      </div>
      {loading ? (
        <div className="text-center py-10 text-[#5C6E64]">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-[#5C6E64] bg-white rounded-2xl border border-[#E4EBE6]">
          لا توجد تعديلات بعد
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E4EBE6] overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-[#F3F6F4] text-[#5C6E64] text-xs">
              <tr>
                <th className="px-3 py-2 text-right">#</th>
                <th className="px-3 py-2 text-right">الزبون</th>
                <th className="px-3 py-2 text-center">قيمة قبل</th>
                <th className="px-3 py-2 text-center">قيمة بعد</th>
                <th className="px-3 py-2 text-center">تغيير الدين</th>
                <th className="px-3 py-2 text-center">عدد البنود</th>
                <th className="px-3 py-2 text-right">عدّله</th>
                <th className="px-3 py-2 text-right">متى</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const direction = r.new_total - r.old_total;
                return (
                  <tr key={r.order_id || i} className="border-t border-[#E4EBE6] hover:bg-[#F9FAFB]" data-testid={`edit-log-row-${r.order_id}`}>
                    <td className="px-3 py-2 text-right">
                      <span className="font-bold text-[#1E3A5F]">#{r.invoice_number ?? r.order_id?.slice(0,8)}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <p className="font-bold">{r.user_name || "—"}</p>
                      {r.market_name && <p className="text-xs text-[#5C6E64]">{r.market_name}</p>}
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-[#5C6E64]" dir="ltr">{fmt(r.old_total)}</td>
                    <td className="px-3 py-2 text-center font-bold" dir="ltr" style={{ color: direction >= 0 ? "#15803D" : "#E85D45" }}>{fmt(r.new_total)}</td>
                    <td className="px-3 py-2 text-center font-bold" dir="ltr" style={{ color: r.debt_changed_by > 0 ? "#E85D45" : r.debt_changed_by < 0 ? "#15803D" : "#5C6E64" }}>
                      {r.debt_changed_by !== 0 && (r.debt_changed_by > 0 ? <TrendingUp className="inline w-3 h-3" /> : <TrendingDown className="inline w-3 h-3" />)}
                      {' '}{r.debt_changed_by > 0 ? '+' : ''}{fmt(r.debt_changed_by)}
                    </td>
                    <td className="px-3 py-2 text-center">{r.items_count}</td>
                    <td className="px-3 py-2 text-right text-xs">{r.edited_by_admin_name || "—"}</td>
                    <td className="px-3 py-2 text-right text-xs text-[#5C6E64]">{fmtDt(r.edited_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceEditsLogTab;
